import React, { FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../redux/store";
import s from "./Toolbar.module.scss";
import { ToolbarSection } from "../../shared/ToolbarSection/ToolbarSection";
import { ActionButton } from "../../shared/ActionButton/ActionButton";
import { DropdownButton } from "../../shared/DropdownButton/DropdownButton";
import { ToolbarBackArrow } from "../../assets/svg/ToolbarBackArrow";
import { ToolbarForArrow } from "../../assets/svg/ToolbarForArrow";
import { ToolbarMove } from "../../assets/svg/ToolbarMove";
import { ToolbarRotate } from "../../assets/svg/ToolbarRotate";
import { ToolbarScale } from "../../assets/svg/ToolbarScale";
import { ToolbarSnap } from "../../assets/svg/ToolbarSnap";
import { ToolbarDimensions } from "../../assets/svg/ToolbarDimensions";
import { ToolbarCamera } from "../../assets/svg/ToolbarCamera";
import { ToolbarDof } from "../../assets/svg/ToolbarDof";
import { ToolbarNet } from "../../assets/svg/ToolbarNet";
import { CameraSettingDropdown } from "../../shared/CameraSettingDropdown/CameraSettingDropdown";
import { CameraPresetDropdown } from "../../shared/CameraPresetDropdown/CameraPresetDropdown";
import { ProjectionDropdown } from "../../shared/ProjectionDropdown/ProjectionDropdown";
import {
  redoAction,
  undoAction,
} from "../../redux/reducers/ui/actions/actions";
import {
  hasUndo,
  hasRedo,
} from "../../redux/reducers/ui/selectors/actionHistorySelectors";
import {
  getCamera,
  getSelectedModel,
  getAspectRatio,
  getWrapperDimensions,
} from "../../redux/reducers/ui/uiSelectors";
import { getSelectDataObjects } from "../../redux/reducers/ui/selectors/objectSelectors";
import {
  dimTypeT,
  playerSettings,
} from "../../utils/functions/playcanvas/playerSettings";
import {
  setSelectedModel,
  updateCameraSetting,
  setCameraConfig,
} from "../../redux/reducers/ui/uiReducer";
import { dimensionsToggle } from "../../utils/functions/playcanvas/dimensionsToggle";
import { setCameraFOV } from "../../utils/functions/playcanvas/setCameraFOV";
import { useSceneLoaded } from "../../hooks/useSceneLoaded";
import { useProjectionZoom } from "../../hooks/useProjectionZoom";
import { TOOLBAR_PROJECTIONS } from "../../utils/toolbarConstants";
import { checkModelSelected } from "../../utils/functions/playcanvas/checkModelSelected";
import { setCameraPosition } from "../../utils/functions/playcanvas/setCameraPosition";
import { setProjection } from "../../utils/functions/playcanvas/setProjection";
import {
  changeShowModal,
  setToolbarReady,
} from "../../redux/reducers/app/appReducer";
import { CameraPreset } from "../../hooks/useCameraPresets";
import { ScreenDimensionsService } from "../../services/ScreenDimensionsService";
import { isPlayCanvasReady } from "../../utils/functions/playcanvas/isPlayCanvasReady";
import { reselectSelected } from "../../utils/functions/playcanvas/reselectSelected";
import {
  readSelectedRotation,
  setRotation,
} from "../../utils/functions/playcanvas/setRotation";
import {
  startMoveRotationGuard,
  stopMoveRotationGuard,
} from "../../utils/functions/playcanvas/moveRotationGuard";
import { getCameraFov } from "../../utils/functions/playcanvas/getCameraFov";

interface ActionItem {
  type: "action";
  props: React.ComponentProps<typeof ActionButton>;
}

interface DropdownItem {
  type: "dropdown";
  props: React.ComponentProps<typeof DropdownButton>;
}

type ToolbarItemConfig = ActionItem | DropdownItem;

interface ToolbarSectionConfig {
  id: string;
  items: ToolbarItemConfig[];
}

// Props for camera presets hook
export interface ToolbarProps {
  presets: CameraPreset[];
  activePreset: CameraPreset | null;
  setActivePreset: (preset: CameraPreset) => void;
  deletePreset: (presetId: string) => Promise<void>;
  savePreset: (customName?: string, forceLocal?: boolean) => Promise<void>;
  loadPresets: () => Promise<void>;
}

export const Toolbar: FC<ToolbarProps> = ({
  presets,
  activePreset,
  setActivePreset,
  deletePreset,
  savePreset,
  loadPresets,
}) => {
  const dispatch = useDispatch();
  const { selectedType, selectedModel } = useAppSelector(getSelectedModel());
  const { sensorWidth, focalDistance, fov, exposure } =
    useAppSelector(getCamera);
  const aspectRatio = useAppSelector(getAspectRatio);
  const wrapperDimensions = useAppSelector(getWrapperDimensions);
  const selectObjects = useAppSelector(getSelectDataObjects);

  const screenService = new ScreenDimensionsService();
  const MAX_FOV = 170;
  const MARGIN = 8;

  const getCurrentFrameWidth = () => {
    if (aspectRatio && aspectRatio.id < 0) {
      return aspectRatio.value?.width || wrapperDimensions.w;
    } else {
      const { frame } = screenService.fitFrameToWrapper(
        wrapperDimensions,
        aspectRatio,
        MARGIN
      );
      return frame.width;
    }
  };

  const cameraSettings = [
    {
      label: "Sensor width",
      unit: "mm",
      min: 1,
      max: 1000,
      defaultValue: sensorWidth,
      step: 0.1,
      showCheckbox: true,
    },
    {
      label: "Focal distance",
      unit: "m",
      min: 1,
      max: 10000,
      defaultValue: focalDistance,
      step: 0.1,
      showCheckbox: true,
    },
    {
      label: "Field of View",
      unit: "°",
      min: 5,
      max: 170,
      defaultValue: fov,
      step: 0.1,
    },
    {
      label: "Exposure",
      unit: "s",
      min: -15,
      max: 15,
      defaultValue: exposure,
      step: 0.1,
      showCheckbox: true,
    },
  ];

  // Check if undo/redo operations are available via selectors
  const canUndo = useAppSelector(hasUndo);
  const canRedo = useAppSelector(hasRedo);

  const [snapType, setSnapType] = useState(false);
  const sceneIsLoad = useSceneLoaded();
  const [dimensionsOn, setDimensionsOn] = useState(false);

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    dispatch(setSelectedModel({ selectedType: "move" }));
    checkModelSelected(dispatch, () => {});
  }, [dispatch]);

  const projections = TOOLBAR_PROJECTIONS;

  const [selectedProjection, setSelectedProjection] =
    useState<string>("perspective");

  // Enable mouse wheel zoom for orthographic projections
  const isOrthographic = selectedProjection !== "perspective";
  useProjectionZoom(isOrthographic && sceneIsLoad);

  const handleUndo = () => {
    if (canUndo) {
      dispatch(undoAction());
    }
  };

  const handleRedo = () => {
    if (canRedo) {
      dispatch(redoAction());
    }
  };

  const handleObjectModeToggle = useCallback(
    (type: dimTypeT) => {
      if (!sceneIsLoad || !selectedModel) return;
      // Preserve current rotation when switching to Move
      let prevRot: { x: number; y: number; z: number } | null = null;
      if (type === "move") prevRot = readSelectedRotation();

      playerSettings({ type });

      if (type === "move") {
        // One-shot reapply to beat immediate normalization
        if (prevRot) {
          requestAnimationFrame(() => setRotation(prevRot!));
          setTimeout(() => setRotation(prevRot!), 25);
        }
        // Guard rotation while dragging in Move
        startMoveRotationGuard();
      } else {
        stopMoveRotationGuard();
      }
      dispatch(setSelectedModel({ selectedType: type }));
    },
    [sceneIsLoad, selectedModel]
  );

  const handleSnapToggle = () => {
    if (!sceneIsLoad || !selectedModel) return;
    const newSnap = !snapType;
    playerSettings({ type: newSnap ? "snapOn" : "snapOff" });
    setSnapType(newSnap);
  };

  // Check whether the Toolbar element is mounted.
  useEffect(() => {
    dispatch(setToolbarReady(true));

    return () => {
      dispatch(setToolbarReady(false));
    };
  }, []);

  useEffect(() => {
    if (!sceneIsLoad) return;
    if (!selectedModel) {
      const iframe = (window as any).containerRef?.current?.contentWindow;
      if (iframe?.SetAutorotation) {
        playerSettings({ type: "snapOff" });
      }
      setSnapType(false);
      stopMoveRotationGuard();
    }
  }, [selectedModel, sceneIsLoad]);

  // Initialize UI FOV from PlayCanvas scene definition
  useEffect(() => {
    let cancelled = false;

    const initFovFromScene = async () => {
      try {
        const sceneFov = await getCameraFov();
        if (!cancelled && typeof sceneFov === "number") {
          dispatch(
            updateCameraSetting({
              key: "fov",
              value: sceneFov,
            })
          );
        }
      } catch (error) {
        console.warn(error);
      }
    };

    initFovFromScene();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Enable Move, Rotate, Scale on 1 / 2 / 3 key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      const isTypingElement =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);

      if (event.repeat || isTypingElement) return;

      switch (event.key) {
        case "1":
          event.preventDefault();

          handleObjectModeToggle("move");
          break;

        case "2":
          event.preventDefault();

          handleObjectModeToggle("rotate");
          break;

        case "3":
          event.preventDefault();

          handleObjectModeToggle("scale");
          break;

        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleObjectModeToggle]);

  const handleDimensionsToggle = () => {
    const newState = !dimensionsOn;
    dimensionsToggle({ show: newState });
    setDimensionsOn(newState);
  };

  const handleDropdownToggle = (dropdownId: string, isOpen: boolean) => {
    setOpenDropdown(isOpen ? dropdownId : null);
  };

  const handleCloseDropdown = () => {
    setOpenDropdown(null);
  };

  const handleProjectionChange = (projectionId: string) => {
    setSelectedProjection(projectionId);
    handleCloseDropdown();

    console.log(`Switching to projection: ${projectionId}`);

    try {
      setProjection(projectionId);

      if (projectionId === "perspective" && isPlayCanvasReady()) {
        const frameWidth = getCurrentFrameWidth();
        const adjustedFOV = screenService.calculateAdjustedFOV(
          fov,
          wrapperDimensions.w,
          frameWidth,
          MAX_FOV
        );
        setCameraFOV(adjustedFOV);
      }
    } catch (error) {
      console.log("Error switching projection:");
    }
  };

  // Custom handler for save camera that opens modal for local presets
  const handleSaveCamera = () => {
    // For now, always open the modal for local presets (templateId is undefined)
    // Later this will be enhanced with actual preset creation logic
    dispatch(
      changeShowModal({
        nameModal: "createPresetModal",
        stateModal: true,
      })
    );
  };

  const hasProducts = Object.keys(selectObjects || {}).length > 0;

  const toolbarConfig: ToolbarSectionConfig[] = [
    {
      id: "navigation",
      items: [
        {
          type: "action",
          props: {
            id: "back",
            icon: <ToolbarBackArrow />,
            onClick: handleUndo,
            disabled: !canUndo,
            "data-tour": "toolbar-undo",
          },
        },
        {
          type: "action",
          props: {
            id: "forward",
            icon: <ToolbarForArrow />,
            onClick: handleRedo,
            disabled: !canRedo,
            "data-tour": "toolbar-redo",
          },
        },
      ],
    },
    {
      id: "motion",
      items: [
        {
          type: "action",
          props: {
            id: "move",
            icon: <ToolbarMove />,
            onClick: () => handleObjectModeToggle("move"),
            isActive: selectedType === "move",
            "data-tour": "toolbar-move",
          },
        },
        {
          type: "action",
          props: {
            id: "rotate",
            icon: <ToolbarRotate />,
            onClick: () => handleObjectModeToggle("rotate"),
            isActive: selectedType === "rotate",
            "data-tour": "toolbar-rotate",
          },
        },
        {
          type: "action",
          props: {
            id: "scale",
            icon: <ToolbarScale />,
            onClick: () => handleObjectModeToggle("scale"),
            isActive: selectedType === "scale",
            "data-tour": "toolbar-scale",
          },
        },
      ],
    },
    {
      id: "size",
      items: [
        {
          type: "action",
          props: {
            id: "snap",
            icon: <ToolbarSnap />,
            onClick: handleSnapToggle,
            isActive: snapType,
            "data-tour": "toolbar-magnet",
          },
        },
        {
          type: "action",
          props: {
            id: "dimensions",
            icon: <ToolbarDimensions />,
            onClick: handleDimensionsToggle,
            isActive: dimensionsOn,
            "data-tour": "toolbar-dimensions",
          },
        },
      ],
    },
    {
      id: "cameras",
      items: [
        {
          type: "dropdown",
          props: {
            id: "camera",
            icon: <ToolbarCamera />,
            label: activePreset ? activePreset.label : "Camera Presets",
            isOpen: openDropdown === "camera",
            onToggle: isOpen => handleDropdownToggle("camera", isOpen),
            "data-tour": "toolbar-camera-settings",
            children: (
              <CameraSettingDropdown
                settings={cameraSettings}
                onChangeSetting={(label, value) => {
                  switch (label) {
                    case "Sensor width":
                      dispatch(
                        updateCameraSetting({ key: "sensorWidth", value })
                      );
                      break;
                    case "Focal distance":
                      dispatch(
                        updateCameraSetting({ key: "focalDistance", value })
                      );
                      break;
                    case "Field of View":
                      dispatch(updateCameraSetting({ key: "fov", value }));
                      if (isPlayCanvasReady()) {
                        // Compute actual frame pixel width and recalc FOV accordingly
                        const frameWidth = getCurrentFrameWidth();
                        const adjustedFOV = screenService.calculateAdjustedFOV(
                          value,
                          wrapperDimensions.w,
                          frameWidth,
                          MAX_FOV
                        );
                        setCameraFOV(adjustedFOV);
                      }
                      break;
                    case "Exposure":
                      dispatch(updateCameraSetting({ key: "exposure", value }));
                      break;
                  }
                }}
                onToggleSetting={(label, checked, value) => {
                  switch (label) {
                    case "Sensor width":
                      dispatch(
                        updateCameraSetting({
                          key: "sensorWidth",
                          value: checked ? value : null,
                        })
                      );
                      break;
                    case "Focal distance":
                      dispatch(
                        updateCameraSetting({
                          key: "focalDistance",
                          value: checked ? value : null,
                        })
                      );
                      break;
                    case "Exposure":
                      dispatch(
                        updateCameraSetting({
                          key: "exposure",
                          value: checked ? value : null,
                        })
                      );
                      break;
                  }
                }}
                onClose={handleCloseDropdown}
              />
            ),
          },
        },
        {
          type: "dropdown",
          props: {
            id: "DOF",
            icon: <ToolbarDof />,
            label: activePreset ? activePreset.label : "Camera Presets",
            isOpen: openDropdown === "DOF",
            onToggle: isOpen => {
              handleDropdownToggle("DOF", isOpen);
              if (isOpen) {
                loadPresets();
              }
            },
            "data-tour": "toolbar-camera-preset",
            children: (
              <CameraPresetDropdown
                presets={presets}
                onSelect={preset => {
                  // Update local active camera for UI label
                  setActivePreset(preset);
                  // 1) Save full camera config to Redux
                  dispatch(setCameraConfig(preset.config));

                  // 2) Apply camera transform in iframe (if transform data exists)
                  if (
                    preset.config.transform?.position &&
                    preset.config.transform?.rotation
                  ) {
                    setCameraPosition({
                      json: {
                        pos: preset.config.transform.position,
                        rot: {
                          x: preset.config.transform.rotation.pitch,
                          y: preset.config.transform.rotation.yaw,
                          z: preset.config.transform.rotation.roll,
                        },
                      },
                    });
                  }

                  // 3) Update FOV in PlayCanvas directly (if FOV data exists)
                  const fov = preset.config.fov;
                  if (fov && isPlayCanvasReady()) {
                    const frameWidth = getCurrentFrameWidth();
                    const adjustedPresetFOV =
                      screenService.calculateAdjustedFOV(
                        fov,
                        wrapperDimensions.w,
                        frameWidth,
                        MAX_FOV
                      );
                    setCameraFOV(adjustedPresetFOV);
                  }

                  // Close dropdown
                  handleCloseDropdown();
                }}
                onClose={handleCloseDropdown}
                onDelete={deletePreset}
                onSave={handleSaveCamera}
              />
            ),
          },
        },
        {
          type: "dropdown",
          props: {
            id: "projection",
            icon: <ToolbarNet />,
            label:
              projections.find(p => p.id === selectedProjection)?.name ||
              "Projection",
            isOpen: openDropdown === "projection",
            onToggle: isOpen => handleDropdownToggle("projection", isOpen),
            "data-tour": "toolbar-projection",
            children: (
              <ProjectionDropdown
                projections={projections}
                selectedProjection={selectedProjection}
                onProjectionChange={handleProjectionChange}
                onClose={handleCloseDropdown}
                hasProducts={hasProducts}
              />
            ),
          },
        },
      ],
    },
  ];

  return (
    <div className={s.toolbar} data-ui-element="toolbar" data-tour="top-toolbar">
      {toolbarConfig.map(section => (
        <ToolbarSection key={section.id} id={section.id}>
          {section.items.map(item =>
            item.type === "action" ? (
              <ActionButton key={item.props.id} {...item.props} />
            ) : (
              <DropdownButton key={item.props.id} {...item.props} />
            )
          )}
        </ToolbarSection>
      ))}
    </div>
  );
};
